import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ticketKeys } from './keys';
import { isStale } from '@/types/investigation';
import type { DismissReason, Investigation, LinearIssueRef, ProposedAction, StaleChange } from '@/types/investigation';
import { extractErrorMessage } from './ticketMutations';
import type { AiSettings } from './settings';

export type TriageDepth = 'reply' | 'light' | 'deep';

export interface TriageUsage {
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number | null;
}

export interface Triage {
  depth: TriageDepth;
  reasoning: string;
  plan: string[];
  skipped: boolean;
  durationMs: number;
  usage: TriageUsage | null;
}

// The snapshot's tiers/triage are the same server object as AiSettings, just without the
// top-level `enabled`/`updatedBy`/`updatedAt` — reuse its shapes rather than a looser duplicate.
export type AiSettingsSnapshot = Pick<AiSettings, 'triage' | 'tiers'>;

// `triage` and `requestedDepth` came in with the triage rollout and are present on every doc
// (list and detail responses alike); `config` is a settings snapshot only the detail endpoint
// returns. The shared Investigation type predates all three, so they're layered on here rather
// than widening that type for every other consumer.
export type InvestigationWithTriage = Investigation & {
  triage: Triage | null;
  requestedDepth: 'auto' | 'deep';
};

export type InvestigationDetail = InvestigationWithTriage & {
  config?: AiSettingsSnapshot;
};

export interface InvestigationListResponse {
  data: InvestigationWithTriage[];
}

// Global QueryClient default staleTime is 30s (main.tsx) — polling hooks need
// their own runs judged fresh on every tick, so they override it to 0.
export function useInvestigations(ticketId: string) {
  return useQuery({
    queryKey: ticketKeys.investigations(ticketId),
    queryFn: () => api<InvestigationListResponse>(`/support-tickets/${ticketId}/investigations`),
    staleTime: 0,
    refetchInterval: query => {
      const runs = query.state.data?.data ?? [];
      const active = runs.some(r => (r.status === 'queued' || r.status === 'running') && !isStale(r));
      return active ? 3000 : false;
    }
  });
}

// List responses project debug out, so the live step feed for the active run
// polls this detail endpoint instead. Debug views on terminal runs (completed/
// failed/cancelled) reuse this same query but pass poll:false — the record is
// static once the run has stopped, so there's nothing to refetch every 3s.
export function useInvestigation(ticketId: string, investigationId: string, enabled: boolean, poll = true) {
  return useQuery({
    queryKey: ticketKeys.investigation(ticketId, investigationId),
    queryFn: () => api<InvestigationDetail>(`/support-tickets/${ticketId}/investigations/${investigationId}`),
    enabled: enabled && Boolean(investigationId),
    staleTime: 0,
    refetchInterval: poll ? 3000 : false
  });
}

export function useStartInvestigation(ticketId: string) {
  const qc = useQueryClient();
  // TVariables is explicitly `| void`, not `| undefined` — only `void` lets callers omit the
  // argument entirely (existing plain re-runs), while still accepting `{ forceDepth: 'deep' }`.
  return useMutation<{ investigationId: string }, Error, { forceDepth?: 'deep' } | void>({
    mutationFn: args =>
      api<{ investigationId: string }>(`/support-tickets/${ticketId}/investigate`, {
        method: 'POST',
        body: args?.forceDepth ? { forceDepth: args.forceDepth } : undefined
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ticketKeys.investigations(ticketId) }),
    onError: err => {
      const status = (err as { status?: number; statusCode?: number } | null)?.status ?? (err as { statusCode?: number } | null)?.statusCode;
      // A 409 also means the API has just swept any stale runs for this ticket, so refetch — the
      // list the panel is showing may already be out of date.
      if (status === 409) {
        toast.error('An investigation is already running');
        qc.invalidateQueries({ queryKey: ticketKeys.investigations(ticketId) });
      } else if (status === 404) toast.error('The Support Agent is not enabled');
      else toast.error((err as Error).message ?? 'Could not start investigation');
    }
  });
}

export interface ProposalErrorInfo {
  code?: 'STALE_PROPOSAL' | 'ALREADY_DECIDED' | string;
  message: string;
  staleChanges?: StaleChange[];
}

// The API's error body is { error: { message, data: { code, staleChanges } } }, which ofetch puts on err.data.
export function proposalError(err: unknown, fallback: string): ProposalErrorInfo {
  const data = (err as { data?: { error?: { data?: { code?: string; staleChanges?: StaleChange[] } } } } | null)?.data?.error?.data;
  return { code: data?.code, staleChanges: data?.staleChanges, message: extractErrorMessage(err, fallback) };
}

export interface DismissInput {
  proposalId: string;
  reason?: DismissReason;
  note?: string;
}

// Both decisions write an internal system message on the ticket, and a refused apply still changes
// the proposal (the API marks it stale), so every outcome refetches the thread and the runs. The
// caller shows the outcome inline, next to the proposal it belongs to.
function useRefreshTicket(ticketId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ticketKeys.investigations(ticketId) });
    qc.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
  };
}

export function useApplyProposal(ticketId: string, invId: string) {
  const refresh = useRefreshTicket(ticketId);
  return useMutation<{ proposal: ProposedAction }, Error, string>({
    mutationFn: proposalId =>
      api<{ proposal: ProposedAction }>(`/support-tickets/${ticketId}/investigations/${invId}/proposals/${proposalId}/apply`, { method: 'POST' }),
    onSettled: refresh
  });
}

export function useDismissProposal(ticketId: string, invId: string) {
  const refresh = useRefreshTicket(ticketId);
  return useMutation<{ proposal: ProposedAction }, Error, DismissInput>({
    mutationFn: ({ proposalId, reason, note }) =>
      api<{ proposal: ProposedAction }>(`/support-tickets/${ticketId}/investigations/${invId}/proposals/${proposalId}/dismiss`, {
        method: 'POST',
        body: { ...(reason ? { reason } : {}), ...(note?.trim() ? { note: note.trim() } : {}) }
      }),
    onSettled: refresh
  });
}

// Files one action as a Linear issue. A repeat click gets the issue already filed, not a second one.
export function useCreateActionLinearIssue(ticketId: string, invId: string) {
  const refresh = useRefreshTicket(ticketId);
  return useMutation<{ linearIssue: LinearIssueRef; alreadyLinked: boolean }, Error, number>({
    mutationFn: actionIndex =>
      api<{ linearIssue: LinearIssueRef; alreadyLinked: boolean }>(`/support-tickets/${ticketId}/investigations/${invId}/actions/${actionIndex}/linear`, {
        method: 'POST'
      }),
    onSuccess: data => toast.success(data.alreadyLinked ? `Already filed as ${data.linearIssue.identifier ?? 'a Linear issue'}` : `Created ${data.linearIssue.identifier ?? 'the Linear issue'}`),
    onError: err => toast.error(extractErrorMessage(err, 'Could not create the Linear issue')),
    onSettled: refresh
  });
}

// Records that the draft went into the composer. It never sends anything.
export function useMarkDraftUsed(ticketId: string, invId: string) {
  const qc = useQueryClient();
  return useMutation<{ draftUsedAt: string }, Error, void>({
    mutationFn: () => api<{ draftUsedAt: string }>(`/support-tickets/${ticketId}/investigations/${invId}/draft-used`, { method: 'POST' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ticketKeys.investigations(ticketId) })
  });
}
